import React, { useEffect, useState } from "react";
import api, { API, getAccessToken, fileDownloadUrl, formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import { useCenter } from "@/context/CenterContext";
import UserAvatar from "@/components/UserAvatar";
import CoursePlayer from "@/components/training/CoursePlayer";
import CourseBuilder from "@/components/training/CourseBuilder";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus, GraduationCap, Lock, CheckCircle2, PlayCircle, Bell, Trash2, Pencil,
  Award, BookOpen, Eye, EyeOff,
} from "lucide-react";
import { toast } from "sonner";

function CatalogCard({ c, isManager, onOpen, onEdit, onDelete, onRemind, onPublish, i }) {
  const { t } = useT();
  const mp = c.my_progress || {};
  const locked = c.locked && !isManager;
  return (
    <Card className={`p-5 flex flex-col animate-fade-up ${locked ? "opacity-70" : ""}`} style={{ animationDelay: `${i * 40}ms` }} data-testid="course-card">
      <div className="flex items-start justify-between">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center">
          <BookOpen className="w-5 h-5 text-primary" />
        </div>
        <div className="flex gap-1 items-center">
          {c.required ? <Badge className="text-[10px]">{t("requiredCourse")}</Badge> : <Badge variant="secondary" className="text-[10px]">{t("optional")}</Badge>}
          {isManager && !c.published && <Badge variant="outline" className="text-[10px]">{t("draft")}</Badge>}
        </div>
      </div>
      <h3 className="font-semibold mt-3">{c.title}</h3>
      <p className="text-xs text-muted-foreground mt-0.5">{c.category} · {c.module_count} {t("modules")} · {c.lesson_count + c.quiz_count} {t("lessonsAndQuizzes")}</p>
      {c.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{c.description}</p>}

      {locked ? (
        <div className="mt-3 flex items-center gap-1.5 text-xs text-amber-600"><Lock className="w-3.5 h-3.5" />{t("completeToUnlock")}: {c.missing_prerequisites.join(", ")}</div>
      ) : (
        <div className="mt-3">
          <Progress value={mp.percent || 0} className="h-1.5" />
          <div className="flex items-center justify-between mt-1 text-xs text-muted-foreground">
            <span>{mp.percent || 0}%</span>
            {mp.completed && <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />{t("completed")}</span>}
          </div>
        </div>
      )}

      <div className="mt-auto pt-4 flex items-center gap-1.5">
        <Button size="sm" className="flex-1" disabled={locked} onClick={() => onOpen(c)} data-testid="open-course">
          <PlayCircle className="w-4 h-4 mr-1.5" />
          {mp.completed ? t("review") : (mp.percent > 0 ? t("continueCourse") : t("startCourse"))}
        </Button>
        {isManager && (
          <>
            <Button variant="ghost" size="icon" onClick={() => onPublish(c)} title={c.published ? t("unpublish") : t("publish")} data-testid="publish-course">{c.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</Button>
            <Button variant="ghost" size="icon" onClick={() => onEdit(c)} data-testid="edit-course"><Pencil className="w-4 h-4" /></Button>
            <Button variant="ghost" size="icon" onClick={() => onRemind(c)} title={t("remind")} data-testid="remind-course"><Bell className="w-4 h-4" /></Button>
            <Button variant="ghost" size="icon" onClick={() => onDelete(c)} data-testid="delete-course"><Trash2 className="w-4 h-4 text-destructive" /></Button>
          </>
        )}
      </div>
    </Card>
  );
}

export default function Training() {
  const { t } = useT();
  const { isManager } = useCenter();
  const [courses, setCourses] = useState([]);
  const [overview, setOverview] = useState(null);
  const [history, setHistory] = useState(null);
  const [tab, setTab] = useState("catalog");
  const [view, setView] = useState({ mode: "list" }); // list | player | builder

  const load = () => {
    api.get("/training/courses").then((r) => setCourses(r.data));
    api.get("/training/my/history").then((r) => setHistory(r.data)).catch(() => {});
    if (isManager) api.get("/training/overview").then((r) => setOverview(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const del = async (c) => { if (!window.confirm(`${t("delete")} "${c.title}"?`)) return; await api.delete(`/training/courses/${c.id}`); load(); };
  const remind = async (c) => { const { data } = await api.post(`/training/courses/${c.id}/remind`); toast.success(`${data.reminded} ✉`); };
  const publish = async (c) => { await api.post(`/training/courses/${c.id}/publish`); load(); };

  if (view.mode === "player")
    return <div className="h-full"><CoursePlayer courseId={view.id} onBack={() => { setView({ mode: "list" }); load(); }} /></div>;
  if (view.mode === "builder")
    return <div className="h-full"><CourseBuilder courseId={view.id} allCourses={courses} onClose={() => setView({ mode: "list" })} onSaved={() => { setView({ mode: "list" }); load(); }} /></div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2"><GraduationCap className="w-7 h-7 text-primary" />{t("training")}</h1>
        </div>
        {isManager && <Button onClick={() => setView({ mode: "builder", id: null })} data-testid="add-course-button"><Plus className="w-4 h-4 mr-1.5" />{t("createCourse")}</Button>}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-5">
        <TabsList>
          <TabsTrigger value="catalog" data-testid="ttab-catalog">{t("catalog")}</TabsTrigger>
          <TabsTrigger value="certificates" data-testid="ttab-certificates">{t("myCertificates")}</TabsTrigger>
          {isManager && <TabsTrigger value="overview" data-testid="ttab-overview">{t("overviewMatrix")}</TabsTrigger>}
        </TabsList>
      </Tabs>

      {tab === "catalog" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {courses.map((c, i) => (
            <CatalogCard key={c.id} c={c} i={i} isManager={isManager}
              onOpen={(x) => setView({ mode: "player", id: x.id })}
              onEdit={(x) => setView({ mode: "builder", id: x.id })}
              onDelete={del} onRemind={remind} onPublish={publish} />
          ))}
          {courses.length === 0 && <p className="col-span-full text-center text-muted-foreground py-12">{t("noCourses")}</p>}
        </div>
      )}

      {tab === "certificates" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {(history?.certificates || []).map((cert) => (
            <Card key={cert.id} className="p-5 flex items-center gap-4" data-testid="certificate-card">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center"><Award className="w-6 h-6 text-white" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{cert.course_title}</p>
                <p className="text-xs text-muted-foreground">{t("yourScore")}: {cert.score}% · {t("issued")}: {cert.issued_at?.slice(0, 10)}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => window.open(`${API}/training/certificates/${cert.id}/pdf?auth=${encodeURIComponent(getAccessToken() || "")}`, "_blank")} data-testid="download-cert">
                <Award className="w-4 h-4 mr-1.5" />PDF
              </Button>
            </Card>
          ))}
          {(!history?.certificates || history.certificates.length === 0) && <p className="col-span-full text-center text-muted-foreground py-12">{t("noCertificates")}</p>}
        </div>
      )}

      {tab === "overview" && isManager && overview && (
        <Card className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-3 font-medium sticky left-0 bg-card">{t("employees")}</th>
                {overview.courses.map((c) => <th key={c.id} className="p-3 font-medium text-center text-xs max-w-[120px] truncate" title={c.title}>{c.title}</th>)}
                <th className="p-3 font-medium text-center">{t("completed")}</th>
              </tr>
            </thead>
            <tbody>
              {overview.employees.map((row) => (
                <tr key={row.user.id} className="border-b border-border/50" data-testid="overview-row">
                  <td className="p-3 sticky left-0 bg-card"><div className="flex items-center gap-2"><UserAvatar name={row.user.name} src={row.user.profile_picture} size={28} /><span className="truncate">{row.user.name}</span></div></td>
                  {overview.courses.map((c) => {
                    const cell = row.cells[c.id];
                    return <td key={c.id} className="p-3 text-center">
                      {cell.completed ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" /> : <span className="text-xs text-muted-foreground">{cell.percent}%</span>}
                    </td>;
                  })}
                  <td className="p-3 text-center font-semibold">{row.completed}/{row.total}</td>
                </tr>
              ))}
              {overview.employees.length === 0 && <tr><td colSpan={99} className="p-6 text-center text-muted-foreground">{t("noData")}</td></tr>}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
